<script setup>
import { formatCurrency } from '../../Utils/numberFormat.js'
import {ref, computed, onMounted, inject} from 'vue'
import { useRouter } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import { useSubscriptionStore } from '@/Store/SubscriptionStore.js'
import { useHttpErrorsHandler } from '@/Composables/httpErrorsHandler.js'
import Loader from '@/Components/Loader.vue'
import axios from 'axios'

const router = useRouter()
const $toast = useToast()
const subscriptionStore = useSubscriptionStore()
const { httpErrorsHandler } = useHttpErrorsHandler()

const loading = ref(true)
const processing = ref(false)
const subscription = ref(null)
const plan = ref(null)

const isActive = computed(() => subscription.value?.active === true)
const isCanceled = computed(() => subscription.value?.canceled === true)
const isOnGracePeriod = computed(() => subscription.value?.on_grace_period === true)

const statusBadge = computed(() => {
    if (isOnGracePeriod.value) {
        return { label: 'Annulé (période de grâce)', color: 'warning' }
    }
    if (isActive.value) {
        return { label: 'Actif', color: 'success' }
    }
    return { label: 'Inactif', color: 'danger' }
})

const stripeStatusBadge = computed(() => {
    const statusMap = {
        active: { label: 'Actif', color: 'success' },
        canceled: { label: 'Annulé', color: 'danger' },
        incomplete: { label: 'Incomplet', color: 'warning' },
        incomplete_expired: { label: 'Expiré', color: 'dark' },
        past_due: { label: 'En retard', color: 'danger' },
        paused: { label: 'En pause', color: 'secondary' },
        trialing: { label: 'Essai', color: 'info' },
        unpaid: { label: 'Impayé', color: 'danger' },
    }
    return statusMap[subscription.value?.stripe_status] || { label: subscription.value?.stripe_status, color: 'secondary' }
})

const formatPrice = (value) => {
    return formatCurrency(value).replace(/,00/, '')
}

onMounted(async () => {
    await fetchSubscription()
})

const fetchSubscription = async () => {
    loading.value = true
    try {
        const response = await axios.get('/api/subscription')
        subscription.value = response.data.subscription?.data || response.data.subscription
        plan.value = response.data.plan?.data || response.data.plan
    } catch (error) {
        subscription.value = null
        plan.value = null
        httpErrorsHandler(error)
    } finally {
        loading.value = false
    }
}

const openBillingPortal = async () => {
    processing.value = true
    try {
        const response = await axios.post('/api/subscription/portal', {
            return_url: window.location.href,
        })
        if (response.data.portal_url) {
            window.location.href = response.data.portal_url
        }
    } catch (error) {
        $toast.error('Erreur lors de l\'ouverture du portail de facturation', {
            position: 'top',
            duration: 5000,
            dismissible: true,
            pauseOnHover: true,
        })
        httpErrorsHandler(error)
        processing.value = false
    }
}

const goToPaywall = () => {
    router.push({ name: 'subscription.paywall' })
}

const brandConfig = inject('brandConfig');
</script>

<template>
    <div class="row">
        <!-- Loader -->
        <div class="col-12" v-if="loading">
            <Loader />
        </div>

        <!-- État sans abonnement -->
        <div class="col-12" v-else-if="!subscription">
            <div class="card">
                <div class="card-body text-center py-5">
                    <div class="profile-user position-relative d-inline-block mx-auto mb-4">
                        <img :src="brandConfig.logoBlackSmall" class="rounded-circle avatar-xl img-thumbnail shadow" alt="user-profile-image">
                    </div>
                    <h4>Aucun abonnement actif</h4>
                    <p class="text-muted mb-4">
                        Vous n'avez pas d'abonnement actif. Choisissez un plan pour accéder à toutes les fonctionnalités.
                    </p>
                    <button class="btn btn-secondary" @click="goToPaywall">
                        <i class="ri-price-tag-3-line me-1"></i>
                        Voir les plans disponibles
                    </button>
                </div>
            </div>
        </div>

        <!-- Contenu avec abonnement -->
        <template v-else>
            <!-- Colonne gauche - Plan actuel -->
            <div class="col-xxl-4 col-lg-5">
                <div class="card">
                    <div class="card-body text-center p-4">
                        <div class="profile-user position-relative d-inline-block mx-auto mb-4">
                            <img :src="brandConfig.logoBlackSmall" class="rounded-circle avatar-xl img-thumbnail border" alt="user-profile-image">
                        </div>
                        <h5 class="fs-17 mb-2">{{ plan?.name || 'Plan Standard' }}</h5>
                        <span
                            class="badge fs-11"
                            :class="'bg-' + statusBadge.color + '-subtle text-' + statusBadge.color"
                        >
                            {{ statusBadge.label }}
                        </span>

                        <div class="mt-3" v-if="plan">
                            <h5 class="mb-0 fw-semibold">
                                {{ formatPrice(plan.price_cents / 100) }}
                                <small class="fs-13 text-muted fw-normal">/ {{ plan.billing_interval_label }}</small>
                            </h5>
                        </div>

                        <div class="mt-3 pt-2 text-start features-list" v-if="plan?.description" v-html="plan.description"></div>

                        <!-- Modules inclus -->
                        <div v-if="plan?.modules && plan.modules.length > 0" class="modules-list mt-3 pt-3 text-start">
                            <small class="text-body d-block mb-2 fw-semibold">Modules inclus :</small>
                            <ul class="list-unstyled mb-0">
                                <li
                                    v-for="module in [...plan.modules].sort((a, b) => a.name.localeCompare(b.name, 'fr'))"
                                    :key="module.id"
                                    class="mb-1 d-flex align-items-center"
                                >
                                    <i class="ri-check-line text-success me-2"></i>
                                    <span class="fs-13">{{ module.name }}</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>

                <!-- Infos rapides -->
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">
                            <i class="ri-information-line me-2 align-middle fs-4"></i>
                            Informations
                        </h4>
                    </div>
                    <div class="card-body">
                        <div class="table-responsive">
                            <table class="table table-md table-borderless mb-0">
                                <tbody>
                                    <tr>
                                        <th class="ps-0" scope="row">Statut Stripe&nbsp;:</th>
                                        <td class="text-body">
                                            <span
                                                class="badge fs-11"
                                                :class="'bg-' + stripeStatusBadge.color + '-subtle text-' + stripeStatusBadge.color"
                                            >
                                                {{ stripeStatusBadge.label }}
                                            </span>
                                        </td>
                                    </tr>
                                    <tr>
                                        <th class="ps-0" scope="row">Créé le&nbsp;:</th>
                                        <td class="text-body">{{ subscription.created_at }}</td>
                                    </tr>
                                    <tr v-if="subscription.ends_at">
                                        <th class="ps-0" scope="row">Se termine le&nbsp;:</th>
                                        <td class="text-body text-warning fw-medium">{{ subscription.ends_at }}</td>
                                    </tr>
                                    <tr v-if="subscription.trial_ends_at">
                                        <th class="ps-0" scope="row">Fin d'essai&nbsp;:</th>
                                        <td class="text-body">{{ subscription.trial_ends_at }}</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Colonne droite - Actions -->
            <div class="col-xxl-8 col-lg-7">
                <div class="card">
                    <div class="card-header align-items-center d-flex">
                        <h4 class="card-title mb-0 flex-grow-1">
                            <i class="ri-settings-3-line me-2 align-middle fs-4"></i>
                            Gérer mon abonnement
                        </h4>
                    </div>
                    <div class="card-body">
                        <!-- Action : Portail Stripe -->
                        <div class="d-flex align-items-center border-bottom pb-3 mb-3">
                            <div class="flex-shrink-0">
                                <div class="avatar-sm">
                                    <div class="avatar-title bg-primary-subtle text-primary rounded">
                                        <i class="ri-bank-card-line fs-18"></i>
                                    </div>
                                </div>
                            </div>
                            <div class="flex-grow-1 ms-3">
                                <h6 class="mb-1">Moyens de paiement & factures</h6>
                                <p class="text-muted mb-0 fs-13">Gérez vos cartes bancaires et consultez vos factures via le portail Stripe.</p>
                            </div>
                            <div class="flex-shrink-0">
                                <button
                                    class="btn btn-soft-primary d-inline-flex align-items-center"
                                    :disabled="processing"
                                    @click="openBillingPortal"
                                >
                                    <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
                                    <i v-else class="ri-external-link-line me-1"></i>
                                    Ouvrir
                                </button>
                            </div>
                        </div>

                        <!-- Action : Changer de plan -->
                        <!-- La modification de plan en self-service est désactivée, l'utilisateur doit contacter l'administrateur -->
                        <div class="d-flex align-items-center border-bottom pb-3 mb-3">
                            <div class="flex-shrink-0">
                                <div class="avatar-sm">
                                    <div class="avatar-title bg-secondary-subtle text-secondary rounded">
                                        <i class="ri-customer-service-2-line fs-18"></i>
                                    </div>
                                </div>
                            </div>
                            <div class="flex-grow-1 ms-3">
                                <h6 class="mb-1">Changer de plan</h6>
                                <p class="text-muted mb-0 fs-13">Pour modifier votre abonnement, veuillez contacter votre administrateur via un ticket.</p>
                            </div>
                            <div class="flex-shrink-0">
                                <button
                                    class="btn btn-soft-secondary d-inline-flex align-items-center"
                                    @click="router.push({ name: 'tickets.create' })"
                                >
                                    <i class="ri-ticket-2-line me-1"></i>
                                    Créer un ticket
                                </button>
                            </div>
                        </div>

                        <!-- Action : Réactiver (si période de grâce) -->
                        <div v-if="isOnGracePeriod" class="d-flex align-items-center">
                            <div class="flex-shrink-0">
                                <div class="avatar-sm">
                                    <div class="avatar-title bg-success-subtle text-success rounded">
                                        <i class="ri-refresh-line fs-18"></i>
                                    </div>
                                </div>
                            </div>
                            <div class="flex-grow-1 ms-3">
                                <h6 class="mb-1">Réactiver l'abonnement</h6>
                                <p class="text-muted mb-0 fs-13">Votre abonnement est annulé mais encore actif. Pour le réactiver, veuillez contacter votre administrateur via un ticket.</p>
                            </div>
                            <div class="flex-shrink-0">
                                <button
                                    class="btn btn-soft-success d-inline-flex align-items-center"
                                    @click="router.push({ name: 'tickets.create' })"
                                >
                                    <i class="ri-ticket-2-line me-1"></i>
                                    Créer un ticket
                                </button>
                            </div>
                        </div>

                        <!-- Action : Annuler (si actif et non annulé) -->
                        <!-- L'annulation en self-service est désactivée, l'utilisateur doit contacter l'administrateur -->
                        <div v-else-if="isActive && !isCanceled" class="d-flex align-items-center">
                            <div class="flex-shrink-0">
                                <div class="avatar-sm">
                                    <div class="avatar-title bg-danger-subtle text-danger rounded">
                                        <i class="ri-customer-service-2-line fs-18"></i>
                                    </div>
                                </div>
                            </div>
                            <div class="flex-grow-1 ms-3">
                                <h6 class="mb-1">Annuler l'abonnement</h6>
                                <p class="text-muted mb-0 fs-13">Pour annuler votre abonnement, veuillez contacter votre administrateur via un ticket.</p>
                            </div>
                            <div class="flex-shrink-0">
                                <button
                                    class="btn btn-soft-danger d-inline-flex align-items-center"
                                    @click="router.push({ name: 'tickets.create' })"
                                >
                                    <i class="ri-ticket-2-line me-1"></i>
                                    Créer un ticket
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Alerte période de grâce -->
                <div v-if="isOnGracePeriod" class="alert alert-warning alert-border-left shadow d-flex align-items-center" role="alert">
                    <i class="ri-alert-line me-3 align-middle fs-16"></i>
                    <div>
                        <strong>Abonnement annulé</strong> - Votre accès reste actif jusqu'au <strong>{{ subscription.ends_at }}</strong>. Après cette date, vous perdrez l'accès aux fonctionnalités premium.
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<style scoped>
.plan-description {
    font-size: 0.8rem;
    color: #6c757d;
}

.plan-description :deep(ul) {
    padding-left: 1.25rem;
    margin-bottom: 0;
}

.plan-description :deep(li) {
    margin-bottom: 0.25rem;
}

.plan-description :deep(p:last-child) {
    margin-bottom: 0;
}

/* Modules List */
.modules-list {
    font-size: 0.875rem;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.fs-13 {
    font-size: 0.8125rem;
}

/* Features List */
.features-list {
    font-size: 0.875rem;
    line-height: 1.5;
}
</style>
